<template>
  <div
    class="d-flex align-center flex-wrap ga-4 mb-4 filter-row"
    :style="cssProps"
  >
    <div class="head-office-container">
      <span class="head-office-label text-body-medium">{{
        $t('content-creator.office-selection.head-office')
      }}</span>
      <v-checkbox
        :model-value="includeHeadOffice"
        :disabled="!isSocialMediaConnected"
        color="#175E51"
        hide-details
        density="compact"
        class="head-office-checkbox"
        @update:model-value="$emit('update:includeHeadOffice', $event)"
      />
    </div>

    <div class="filter-dropdown">
      <v-menu
        :close-on-content-click="false"
        :attach="true"
      >
        <template #activator="{ props }">
          <button
            type="button"
            class="dropdown-trigger"
            v-bind="props"
          >
            <span class="dropdown-text text-body-medium">{{
              $t('content-creator.office-selection.select-state')
            }}</span>
            <v-icon
              size="20"
              class="dropdown-icon"
            >
              mdi-chevron-down
            </v-icon>
          </button>
        </template>
        <v-list class="filter-menu-list">
          <v-list-item
            v-for="state in uniqueStates"
            :key="state"
            @click="$emit('toggleState', state)"
          >
            <template #prepend>
              <v-checkbox
                :model-value="selectedStates.includes(state)"
                color="#175E51"
                hide-details
                density="compact"
                class="filter-checkbox"
                @click.stop="$emit('toggleState', state)"
              />
            </template>
            <v-list-item-title>{{ state }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="filter-dropdown">
      <v-menu
        :close-on-content-click="false"
        :attach="true"
      >
        <template #activator="{ props }">
          <button
            type="button"
            class="dropdown-trigger"
            :disabled="selectedStates.length === 0"
            v-bind="props"
          >
            <span class="dropdown-text text-body-medium">{{
              $t('content-creator.office-selection.select-city')
            }}</span>
            <v-icon
              size="20"
              class="dropdown-icon"
            >
              mdi-chevron-down
            </v-icon>
          </button>
        </template>
        <v-list class="filter-menu-list">
          <v-list-item
            v-for="city in availableCities"
            :key="city"
            @click="$emit('toggleCity', city)"
          >
            <template #prepend>
              <v-checkbox
                :model-value="selectedCities.includes(city)"
                color="#175E51"
                hide-details
                density="compact"
                class="filter-checkbox"
                @click.stop="$emit('toggleCity', city)"
              />
            </template>
            <v-list-item-title>{{ city }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-btn
      variant="outlined"
      class="clear-filter-btn"
      @click="$emit('clearFilters')"
    >
      {{ $t('content-creator.office-selection.clear-filters') }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from 'vuetify'
interface Props {
  includeHeadOffice: boolean
  uniqueStates: string[]
  selectedStates: string[]
  availableCities: string[]
  selectedCities: string[]
  isSocialMediaConnected?: boolean
}

defineProps<Props>()

defineEmits<{
  'update:includeHeadOffice': [value: boolean]
  toggleState: [state: string]
  toggleCity: [city: string]
  clearFilters: []
}>()

const theme = useTheme()
const cssProps = computed(() => ({
  '--primary-color': theme.current.value.colors.primary,
}))
</script>

<style scoped>
.filter-row {
  flex-shrink: 0;
  flex-grow: 0;
  position: relative;
  z-index: 10;
}

.head-office-container {
  height: 46px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.head-office-checkbox {
  margin: 0;
}

.head-office-checkbox :deep(.v-selection-control) {
  min-height: 18px;
}

.head-office-checkbox :deep(.v-selection-control__wrapper) {
  width: 18px;
  height: 18px;
}

.head-office-checkbox :deep(.v-selection-control__input) {
  width: 18px;
  height: 18px;
}

.head-office-checkbox :deep(.v-icon) {
  font-size: 18px;
}

.head-office-label {
  color: rgba(var(--v-theme-on-surface));
}

.filter-dropdown {
  display: flex;
  align-items: center;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 0;
}

.dropdown-trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dropdown-text {
  color: rgba(var(--v-theme-on-surface));
}

.dropdown-icon {
  color: rgba(var(--v-theme-on-surface));
}

.filter-checkbox :deep(.v-selection-control__wrapper) {
  width: 18px;
  height: 18px;
}

.filter-checkbox :deep(.v-selection-control__input) {
  width: 18px;
  height: 18px;
}

.filter-checkbox :deep(.v-icon) {
  font-size: 18px;
}

.clear-filter-btn {
  color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-family: Roboto, sans-serif;
  font-weight: 500;
  text-transform: uppercase;
}

.clear-filter-btn:hover {
  background-color: rgba(23, 94, 81, 0.04);
}
</style>

<style>
.filter-menu-list {
  max-height: 300px !important;
  overflow-y: auto;
}

.filter-menu-list .v-list-item {
  min-height: 40px;
  cursor: pointer;
}

.filter-menu-list .v-list-item__prepend {
  width: auto;
  margin-right: 8px;
}

.filter-menu-list .v-list-item-title {
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: rgba(rgba(var(--v-theme-on-surface)));
}

.filter-menu-list .v-selection-control--dirty .v-icon {
  color: #175e51 !important;
}

.filter-menu-list .v-selection-control__wrapper {
  width: 18px;
  height: 18px;
}

.filter-menu-list .v-selection-control__input {
  width: 18px;
  height: 18px;
}

.filter-menu-list .v-icon {
  font-size: 18px;
}
</style>
